/**
 * DRF pagination helpers.
 *
 * Every list endpoint on this API is paginated (`PAGE_SIZE: 25`), so a raw
 * `response.data` is a page envelope, not an array. Calling `.filter()` or
 * `.map()` on it throws — which is exactly the bug that shipped in
 * `alertsApi.getAlertRules`.
 *
 * Some endpoints are not paginated, and a few return a bare array, so these
 * helpers accept both shapes rather than assuming.
 */

/** DRF page envelope for paginated list endpoints. */
export interface Paginated<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export function isPaginated<T>(data: unknown): data is Paginated<T> {
  return (
    typeof data === "object" &&
    data !== null &&
    Array.isArray((data as Paginated<T>).results)
  );
}

/**
 * The rows from a list response, whether it arrived as a page envelope or a
 * bare array. Returns page 1 only — use a `next`-walking loop when the caller
 * genuinely needs every row (see `credentialsApi.list`).
 */
export function unwrapList<T>(data: unknown): T[] {
  if (isPaginated<T>(data)) return data.results;
  return Array.isArray(data) ? (data as T[]) : [];
}

/**
 * The rows *and* how many there are in total.
 *
 * {@link unwrapList} drops `count`, which is the one field that says whether the
 * caller is looking at everything. A screen that shows a page and says nothing
 * is not neutral: it reports a total that happens to be a page size, and on a
 * money surface that reads as the truth.
 *
 * `count` falls back to the number of rows returned, which is exact for a bare
 * array and for a single-page response, and never overstates.
 */
export function unwrapPage<T>(data: unknown): { rows: T[]; count: number } {
  const rows = unwrapList<T>(data);
  const count = isPaginated<T>(data) ? data.count : undefined;
  return { rows, count: typeof count === "number" ? count : rows.length };
}
